import { Controller } from "@hotwired/stimulus"

// Shared AudioContext
const audioCtx = new (window.AudioContext || window.webkitAudioContext)()

export default class extends Controller<HTMLElement> {
    static values = { type: String }

    declare readonly typeValue: string

    connect(): void {
        this.playSound(this.typeValue)
        // Remove the element after a short delay to keep DOM clean
        setTimeout(() => this.element.remove(), 2000)
    }

    playSound(type: string): void {
        if (audioCtx.state === 'suspended') {
            audioCtx.resume()
        }

        switch (type) {
            case "correct":
                this.playCorrectSound()
                break
            case "wrong":
                this.playWrongSound()
                break
            case "level_up":
                this.playLevelUpSound()
                break
            case "game_over":
                this.playGameOverSound()
                break
        }
    }

    playCorrectSound(): void {
        const osc = audioCtx.createOscillator()
        const gain = audioCtx.createGain()

        osc.connect(gain)
        gain.connect(audioCtx.destination)

        osc.type = "sine"
        osc.frequency.setValueAtTime(880, audioCtx.currentTime) // A5
        osc.frequency.exponentialRampToValueAtTime(1760, audioCtx.currentTime + 0.1) // A6

        gain.gain.setValueAtTime(0.3, audioCtx.currentTime)
        gain.gain.exponentialRampToValueAtTime(0.01, audioCtx.currentTime + 0.5)

        osc.start()
        osc.stop(audioCtx.currentTime + 0.5)
    }

    playWrongSound(): void {
        const osc = audioCtx.createOscillator()
        const gain = audioCtx.createGain()

        osc.connect(gain)
        gain.connect(audioCtx.destination)

        osc.type = "sawtooth"
        osc.frequency.setValueAtTime(150, audioCtx.currentTime)
        osc.frequency.linearRampToValueAtTime(100, audioCtx.currentTime + 0.3)

        gain.gain.setValueAtTime(0.3, audioCtx.currentTime)
        gain.gain.exponentialRampToValueAtTime(0.01, audioCtx.currentTime + 0.3)

        osc.start()
        osc.stop(audioCtx.currentTime + 0.3)
    }

    playLevelUpSound(): void {
        const now = audioCtx.currentTime
        const notes = [523.25, 659.25, 783.99, 1046.50] // C E G C

        notes.forEach((freq, i) => {
            const osc = audioCtx.createOscillator()
            const gain = audioCtx.createGain()

            osc.connect(gain)
            gain.connect(audioCtx.destination)

            osc.type = "square"
            osc.frequency.value = freq

            const time = now + (i * 0.1)
            gain.gain.setValueAtTime(0.2, time)
            gain.gain.exponentialRampToValueAtTime(0.01, time + 0.3)

            osc.start(time)
            osc.stop(time + 0.3)
        })
    }

    playGameOverSound(): void {
        const osc = audioCtx.createOscillator()
        const gain = audioCtx.createGain()

        osc.connect(gain)
        gain.connect(audioCtx.destination)

        osc.type = "triangle"
        osc.frequency.setValueAtTime(440, audioCtx.currentTime)
        osc.frequency.exponentialRampToValueAtTime(110, audioCtx.currentTime + 1.0)

        gain.gain.setValueAtTime(0.3, audioCtx.currentTime)
        gain.gain.linearRampToValueAtTime(0.01, audioCtx.currentTime + 1.0)

        osc.start()
        osc.stop(audioCtx.currentTime + 1.0)
    }
}
